import { commitMutation, fetchQuery, graphql } from "relay-runtime"
import { create } from "zustand"
import { createJSONStorage, devtools, persist } from "zustand/middleware"
import { broadcastCallStatus } from "@/lib/call-status-broadcast"
import { getInsensitiveSearchParams } from "@/lib/get-insensitive-search-params"
import { debouncedValidatePhoneNumber } from "@/lib/validate-phone-number"
import { TelephonySearchParams } from "@/modules/patients/constants/Patient"
import { Country } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { RelayEnvironment } from "@/relay-environment"
import { useCallContextStoreChCreateInboundCallMutation } from "@/stores/__generated__/useCallContextStoreChCreateInboundCallMutation.graphql"
import { useCallContextStoreDeCreateInboundCallMutation } from "@/stores/__generated__/useCallContextStoreDeCreateInboundCallMutation.graphql"
import { useCallContextStoreVerifyIgnorePhoneNumberQuery } from "@/stores/__generated__/useCallContextStoreVerifyIgnorePhoneNumberQuery.graphql"
import { CallerRole, Language } from "@/types/__generated__/union-types"

interface CallContextStoreActions {
  clearStore: VoidFunction
  setCountry: (value: Country) => void
  setCallerPhoneNumber: (value: string, isCallerPhoneNumberValid: boolean) => void
  setTemporaryNotes: (value: string) => void
  toggleKidsLine: (v: boolean) => void
  setCallLineCode: (v: string) => void
  setRecord: (record?: string | null) => void
  setCallLineLanguage: (language: Language | undefined) => void
  setCallID: (callID?: string | null) => void
  setCallerRole: (callerRole?: CallerRole | null) => void
  setPayline: (payline?: string | null) => void
  setStartedOn: () => void
  setPushedUrl: (params: string) => void
  setInboundCallId: (inboundCallId: number | null) => void
}

interface ICallContextStore {
  country: Country
  callerPhoneNumber: string
  isCallerPhoneNumberValid: boolean
  isKidsLine: boolean
  temporaryNotes: string
  callLineCode: string
  record?: string | null
  callLineLanguage?: Language
  callID?: string | null
  callerRole?: CallerRole | null
  payline?: string | null
  inboundCallStartedAt: Date
  pushedUrl: string
  inboundCallId: number | null
}

const initialState: ICallContextStore = {
  country: "CH",
  callerPhoneNumber: "",
  isCallerPhoneNumberValid: false,
  temporaryNotes: "",
  isKidsLine: false,
  callLineCode: "",
  payline: "",
  inboundCallStartedAt: new Date(),
  pushedUrl: "",
  callID: "",
  callerRole: null,
  inboundCallId: null,
}

const useCallContextStore = create<ICallContextStore & CallContextStoreActions>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setCountry: (country) => {
          set({ country })
        },
        setCallerPhoneNumber: (callerPhoneNumber, isCallerPhoneNumberValid) => {
          set({ callerPhoneNumber, isCallerPhoneNumberValid })
        },
        setTemporaryNotes: (temporaryNotes) => {
          set({ temporaryNotes })
        },
        setCallLineCode: (callLineCode) => {
          set({ callLineCode })
        },
        clearStore: () => set(initialState),
        toggleKidsLine: (v) => {
          set(!v ? initialState : { isKidsLine: v })
        },
        setRecord: (record) => set({ record }),
        setCallLineLanguage: (callLineLanguage) => set({ callLineLanguage }),
        setCallID: (callID) => set({ callID }),
        setCallerRole: (callerRole) => set({ callerRole }),
        setPayline: (payline) => set({ payline }),
        setStartedOn: () => set({ inboundCallStartedAt: new Date() }),
        setPushedUrl: (pushedUrl) => set({ pushedUrl }),
        setInboundCallId: (inboundCallId) => set({ inboundCallId }),
      }),
      { name: "callContextStore", storage: createJSONStorage(() => sessionStorage) },
    ),
  ),
)

const verifyIgnorePhoneNumber = (phoneNumber: string) => {
  return fetchQuery<useCallContextStoreVerifyIgnorePhoneNumberQuery>(
    RelayEnvironment,
    graphql`
      query useCallContextStoreVerifyIgnorePhoneNumberQuery($phoneNumber: String!) {
        verifyIgnorePhoneNumber(phoneNumber: $phoneNumber)
      }
    `,
    { phoneNumber },
  ).toPromise()
}

type InboundCallQueryParameters = {
  phoneNumber: string | null
  callLine: string | null
  callId: string | null
  country: string | null
  record: string | null
  pl: string | null
  lang: string | null
}

const createInboundCall = (queryPath: string, country: Country, queryParameters: InboundCallQueryParameters) => {
  if (country === "DE") {
    commitMutation<useCallContextStoreDeCreateInboundCallMutation>(RelayEnvironment, {
      mutation: graphql`
        mutation useCallContextStoreDeCreateInboundCallMutation($input: De_createInboundCallInput!) {
          de_createInboundCall(input: $input) {
            createInboundCallOutput {
              inboundCallId
            }
          }
        }
      `,
      variables: { input: { queryPath, queryParameters } },
      onCompleted: (response) => {
        const id = response.de_createInboundCall.createInboundCallOutput?.inboundCallId ?? null
        useCallContextStore.getState().setInboundCallId(id)
      },
    })
    return
  }

  commitMutation<useCallContextStoreChCreateInboundCallMutation>(RelayEnvironment, {
    mutation: graphql`
      mutation useCallContextStoreChCreateInboundCallMutation($input: Ch_createInboundCallInput!) {
        ch_createInboundCall(input: $input) {
          createInboundCallOutput {
            inboundCallId
          }
        }
      }
    `,
    variables: { input: { queryPath, queryParameters } },
    onCompleted: (response) => {
      const id = response.ch_createInboundCall.createInboundCallOutput?.inboundCallId ?? null
      useCallContextStore.getState().setInboundCallId(id)
    },
  })
}

const buildInboundCallQueryParameters = (
  query: URLSearchParams,
  countryParam: string | null,
  phoneNumberWithPrefix: string,
): InboundCallQueryParameters => ({
  phoneNumber: phoneNumberWithPrefix,
  callLine: getInsensitiveSearchParams(query, TelephonySearchParams.CallLine).value,
  callId: getInsensitiveSearchParams(query, TelephonySearchParams.CallId).value,
  country: countryParam,
  record: getInsensitiveSearchParams(query, TelephonySearchParams.Record).value,
  pl: getInsensitiveSearchParams(query, TelephonySearchParams.PayLine).value,
  lang: getInsensitiveSearchParams(query, TelephonySearchParams.Language).value,
})

const validateCallerPhoneNumber = debouncedValidatePhoneNumber()

const resolveCallerPhoneNumber = async (phoneNumberWithPrefix: string, hasPlusPrefixAndDigitsOnly: boolean) => {
  if (!hasPlusPrefixAndDigitsOnly) {
    useCallContextStore.getState().setCallerPhoneNumber("", false)
    return
  }

  const verifyIgnorePhoneNumberResult = await verifyIgnorePhoneNumber(phoneNumberWithPrefix)
  if (verifyIgnorePhoneNumberResult?.verifyIgnorePhoneNumber) {
    useCallContextStore.getState().setCallerPhoneNumber("", false)
    return
  }

  const isCallerPhoneNumberValid = await Promise.resolve(validateCallerPhoneNumber(phoneNumberWithPrefix)).catch(
    () => false,
  )
  useCallContextStore.getState().setCallerPhoneNumber(phoneNumberWithPrefix, isCallerPhoneNumberValid)
}

const handleInsensitiveInboundCallParam = async () => {
  if (!window || !window.location) {
    return
  }

  const query = new URLSearchParams(window.location.search)
  const phoneNumberParam = getInsensitiveSearchParams(query, TelephonySearchParams.PhoneNumber)

  if (!phoneNumberParam.value) return

  // An inbound call always takes the microphone for a phone transcription, so broadcast
  // "phone" just like an outbound call does — this ends any video-call transcription
  // running in a popup window opened from another tab.
  broadcastCallStatus({ channel: "phone" })

  const pushedUrl = window.location.href
  useCallContextStore.getState().setPushedUrl(pushedUrl)

  const countryParam = getInsensitiveSearchParams(query, TelephonySearchParams.Country).value
  const country: Country = countryParam?.toUpperCase() === "DE" ? "DE" : "CH"

  const phoneNumberValue = phoneNumberParam.value.replaceAll(" ", "")
  const phoneNumberWithPrefix = phoneNumberValue.startsWith("+") ? phoneNumberValue : `+${phoneNumberValue}`
  const hasPlusPrefixAndDigitsOnly = /^\+\d+$/.test(phoneNumberWithPrefix)

  createInboundCall(pushedUrl, country, buildInboundCallQueryParameters(query, countryParam, phoneNumberWithPrefix))

  await resolveCallerPhoneNumber(phoneNumberWithPrefix, hasPlusPrefixAndDigitsOnly)

  query.delete(phoneNumberParam.key)
  const url = location.pathname + "?" + query.toString()
  window.history.replaceState({}, "", url)
}

export { handleInsensitiveInboundCallParam }
export default useCallContextStore
